import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, CreditCard, Mail, Users, ImageIcon,
  Settings, Monitor, LogOut, Plus, Edit, Trash2, X, ChevronUp, ChevronDown,
  Smartphone, Check, ArrowUpDown, BarChart3, Upload, Link2, Loader2,
  FileText, Eye, EyeOff, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { getAreaCode, lookupAreaCode } from '@/utils/areaCodes';
import { resolveMediaUrl, isVideoUrl } from '@/utils/media';

const FileUpload = ({ value, onChange, accept = "image/*,video/*", label = "Media" }) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value && !value.startsWith('/api/files/') ? 'url' : 'upload');

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
      toast.success(`Uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-gray-400 text-xs">{label}</Label>
        <div className="flex gap-1">
          <button type="button" onClick={() => setMode('upload')} className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${mode === 'upload' ? 'bg-[#FF2D78] text-white' : 'bg-white/5 text-gray-500'}`}>
            <Upload size={10} className="inline mr-1" />Upload
          </button>
          <button type="button" onClick={() => setMode('url')} className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${mode === 'url' ? 'bg-[#FF2D78] text-white' : 'bg-white/5 text-gray-500'}`}>
            <Link2 size={10} className="inline mr-1" />URL
          </button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div>
          <label className={`flex items-center justify-center gap-2 p-3 border border-dashed border-white/20 cursor-pointer hover:border-[#FF2D78]/50 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            {uploading ? <Loader2 size={16} className="text-[#FF2D78] animate-spin" /> : <Upload size={16} className="text-gray-400" />}
            <span className="text-gray-400 text-xs">{uploading ? 'Uploading...' : 'Choose file (photos & videos)'}</span>
            <input type="file" accept={accept} onChange={handleFileChange} className="hidden" data-testid="file-upload-input" />
          </label>
        </div>
      ) : (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... or /api/files/..."
          className="bg-white/5 border-white/10 text-white text-xs"
        />
      )}

      {value && (
        <div className="mt-1">
          {isVideoUrl(value) ? (
            <video src={resolveMediaUrl(value)} className="w-full h-24 object-cover bg-black" muted />
          ) : (
            <img src={resolveMediaUrl(value)} alt="" className="w-full h-24 object-cover bg-black" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
        </div>
      )}
    </div>
  );
};

const PANELS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'crawls', label: 'Crawls', icon: Calendar },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'bookings', label: 'Bookings', icon: CreditCard },
  { id: 'subscribers', label: 'Subscribers', icon: Mail },
  { id: 'inquiries', label: 'Group Bookings', icon: Users },
  { id: 'gallery', label: 'Gallery', icon: ImageIcon },
  { id: 'config', label: 'Site Config', icon: Settings },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'preview', label: 'Preview', icon: Monitor },
];

const AdminDashboard = ({ auth }) => {
  const navigate = useNavigate();
  const [panel, setPanel] = useState('overview');
  const [stats, setStats] = useState(null);
  const [crawls, setCrawls] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [siteConfig, setSiteConfig] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statsR, crawlsR, bookingsR, subsR, inqR, galR, cfgR, blogR, leadsR] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/crawls`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/subscribers`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/site-config`),
        axios.get(`${API}/blog/admin/all`),
        axios.get(`${API}/admin/all-leads`),
      ]);
      setStats(statsR.data);
      setCrawls(crawlsR.data);
      setBookings(bookingsR.data);
      setSubscribers(subsR.data);
      setInquiries(inqR.data);
      setGallery(galR.data);
      setSiteConfig(cfgR.data);
      setBlogPosts(blogR.data);
      setAllLeads(leadsR.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleLogout = () => { auth.logout(); navigate('/'); toast.success('Logged out'); };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="text-white">Loading...</div></div>;

  const panelTitle = PANELS.find(p => p.id === panel)?.label || '';

  return (
    <div className="flex min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-60 bg-[#0a0a0a] border-r border-white/10 flex flex-col flex-shrink-0">
        <div className="p-5 border-b border-white/10">
          <img src="/cbc-logo.png" alt="CBC" className="w-28" />
          <p className="text-gray-500 text-[10px] mt-2 uppercase tracking-[3px] font-mono">Admin Panel</p>
        </div>
        <nav className="flex-1 py-3">
          {PANELS.map(item => (
            <button
              key={item.id}
              onClick={() => setPanel(item.id)}
              data-testid={`admin-nav-${item.id}`}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-left text-sm transition-colors ${
                panel === item.id ? 'bg-[#FF2D78]/10 text-[#FF2D78] border-r-2 border-[#FF2D78]' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={16} />
              <span className="font-body text-[13px]">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-gray-400 hover:text-red-400 text-sm w-full">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <header className="px-8 py-5 border-b border-white/10 flex items-center justify-between">
          <h1 className="font-display text-2xl text-white uppercase">{panelTitle}</h1>
          <span className="text-gray-500 text-sm">Welcome, {auth.user?.name}</span>
        </header>
        <div className="p-8">
          {panel === 'overview' && <OverviewPanel stats={stats} />}
          {panel === 'crawls' && <CrawlsPanel crawls={crawls} onRefresh={fetchAll} />}
          {panel === 'blog' && <BlogPanel posts={blogPosts} onRefresh={fetchAll} />}
          {panel === 'bookings' && <BookingsPanel bookings={bookings} crawls={crawls} onRefresh={fetchAll} />}
          {panel === 'subscribers' && <SubscribersPanel subscribers={subscribers} allLeads={allLeads} />}
          {panel === 'inquiries' && <InquiriesPanel inquiries={inquiries} onRefresh={fetchAll} />}
          {panel === 'gallery' && <GalleryPanel gallery={gallery} onRefresh={fetchAll} />}
          {panel === 'config' && <SiteConfigPanel config={siteConfig} onRefresh={fetchAll} />}
          {panel === 'analytics' && <AnalyticsPanel />}
          {panel === 'preview' && <PreviewPanel />}
        </div>
      </main>
    </div>
  );
};

/* ==================== OVERVIEW ==================== */
const OverviewPanel = ({ stats }) => {
  const cards = [
    { label: 'Total Crawls', value: stats?.crawl_count || 0, color: '#FF2D78' },
    { label: 'Total Bookings', value: stats?.booking_count || 0, color: '#1E90FF' },
    { label: 'Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, color: '#22C55E' },
    { label: 'Subscribers', value: stats?.subscriber_count || 0, color: '#00F5FF' },
    { label: 'Pending Inquiries', value: stats?.pending_inquiries || 0, color: '#F59E0B' },
  ];
  return (
    <div data-testid="admin-overview-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map(c => (
          <div key={c.label} className="bg-[#0a0a0a] border border-white/10 p-5">
            <p className="text-gray-500 text-xs uppercase tracking-widest">{c.label}</p>
            <p className="font-display text-3xl mt-1" style={{ color: c.color }}>{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================== CRAWLS ==================== */
const CrawlsPanel = ({ crawls, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCrawl, setEditCrawl] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this crawl?')) return;
    try { await axios.delete(`${API}/crawls/${id}`); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEdit = (crawl) => { setEditCrawl(crawl); setDialogOpen(true); };
  const openCreate = () => { setEditCrawl(null); setDialogOpen(true); };

  return (
    <div data-testid="admin-crawls-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400 text-sm">{crawls.length} crawls total</p>
        <Button onClick={openCreate} className="bg-[#FF2D78] hover:bg-[#ff4d8d]" data-testid="add-crawl-btn">
          <Plus size={16} className="mr-2" /> Add Crawl
        </Button>
      </div>
      <div className="space-y-3">
        {crawls.map(c => (
          <div key={c.id} className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-white/10">
            <div className="flex items-center gap-4">
              <img src={c.image_url} alt="" className="w-14 h-14 object-cover" />
              <div>
                <h3 className="text-white font-bold text-sm">{c.title}</h3>
                <p className="text-gray-500 text-xs">{c.city} · {c.neighborhood} · {c.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={c.is_featured ? 'bg-[#FF2D78]/20 text-[#FF2D78]' : 'bg-white/5 text-gray-500'}>
                {c.is_featured ? 'Featured' : 'Standard'}
              </Badge>
              <Badge className={c.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                {c.is_active ? 'Active' : 'Inactive'}
              </Badge>
              <span className="text-gray-500 text-xs w-20 text-right">{c.spots_remaining}/{c.spots_total}</span>
              <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-crawl-${c.id}`}><Edit size={14} className="text-gray-400" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} data-testid={`delete-crawl-${c.id}`}><Trash2 size={14} className="text-red-400" /></Button>
            </div>
          </div>
        ))}
      </div>
      <CrawlFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} crawl={editCrawl} onRefresh={onRefresh} />
    </div>
  );
};

const CrawlFormDialog = ({ open, onClose, crawl, onRefresh }) => {
  const isEdit = !!crawl;
  const defaultForm = { city:'', neighborhood:'', title:'', description:'', date:'', time:'', bars_count:5, price_general:35, price_vip:65, spots_total:100, spots_remaining:100, image_url:'', perks:[], bar_route:[], faqs:[], is_featured:false, is_active:true, ticket_link:'' };
  const [form, setForm] = useState(defaultForm);
  const [perksText, setPerksText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (crawl) { setForm(crawl); setPerksText((crawl.perks || []).join('\n')); }
    else { setForm(defaultForm); setPerksText(''); }
  }, [crawl, open]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, perks: perksText.split('\n').filter(Boolean), bar_route: form.bar_route || [], faqs: form.faqs || [], ticket_link: form.ticket_link || null };
    try {
      if (isEdit) await axios.put(`${API}/crawls/${crawl.id}`, payload);
      else await axios.post(`${API}/crawls`, payload);
      toast.success(isEdit ? 'Updated!' : 'Created!');
      onClose(); onRefresh();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl uppercase">{isEdit ? 'Edit Crawl' : 'Add Crawl'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSave} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-gray-400 text-xs">City</Label><Input value={form.city} onChange={e => f('city', e.target.value)} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label className="text-gray-400 text-xs">Neighborhood</Label><Input value={form.neighborhood} onChange={e => f('neighborhood', e.target.value)} className="bg-white/5 border-white/10 mt-1" /></div>
          </div>
          <div><Label className="text-gray-400 text-xs">Title</Label><Input value={form.title} onChange={e => f('title', e.target.value)} className="bg-white/5 border-white/10 mt-1" /></div>
          <div><Label className="text-gray-400 text-xs">Description</Label><Textarea value={form.description} onChange={e => f('description', e.target.value)} className="bg-white/5 border-white/10 mt-1" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-gray-400 text-xs">Date</Label><Input type="date" value={form.date} onChange={e => f('date', e.target.value)} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label className="text-gray-400 text-xs">Time</Label><Input value={form.time} onChange={e => f('time', e.target.value)} placeholder="8:00 PM" className="bg-white/5 border-white/10 mt-1" /></div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div><Label className="text-gray-400 text-xs">General $</Label><Input type="number" value={form.price_general} onChange={e => f('price_general', parseFloat(e.target.value) || 0)} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label className="text-gray-400 text-xs">VIP $</Label><Input type="number" value={form.price_vip} onChange={e => f('price_vip', parseFloat(e.target.value) || 0)} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label className="text-gray-400 text-xs">Total Spots</Label><Input type="number" value={form.spots_total} onChange={e => f('spots_total', parseInt(e.target.value) || 0)} className="bg-white/5 border-white/10 mt-1" /></div>
            <div><Label className="text-gray-400 text-xs">Spots Left</Label><Input type="number" value={form.spots_remaining} onChange={e => f('spots_remaining', parseInt(e.target.value) || 0)} className="bg-white/5 border-white/10 mt-1" /></div>
          </div>
          <FileUpload value={form.image_url} onChange={(v) => f('image_url', v)} label="Crawl Image / Video" />
          <div>
            <Label className="text-gray-400 text-xs">Ticket Link (optional — overrides built-in checkout)</Label>
            <Input value={form.ticket_link || ''} onChange={e => f('ticket_link', e.target.value)} placeholder="https://eventbrite.com/..." className="bg-white/5 border-white/10 mt-1" data-testid="crawl-ticket-link-input" />
            <p className="text-gray-600 text-[10px] mt-1">If set, "GET TICKETS" buttons will link here instead of Stripe checkout</p>
          </div>
          <div><Label className="text-gray-400 text-xs">Perks (one per line)</Label><Textarea value={perksText} onChange={e => setPerksText(e.target.value)} className="bg-white/5 border-white/10 mt-1 min-h-[80px]" /></div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={v => f('is_featured', v)} /><Label className="text-gray-400 text-xs">Featured</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => f('is_active', v)} /><Label className="text-gray-400 text-xs">Active</Label></div>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#FF2D78] hover:bg-[#ff4d8d]">{saving ? 'Saving...' : isEdit ? 'Update Crawl' : 'Create Crawl'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ==================== BLOG CMS ==================== */
const BlogPanel = ({ posts, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try { await axios.delete(`${API}/blog/${id}`); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed to delete'); }
  };

  const openEdit = (post) => { setEditPost(post); setDialogOpen(true); };
  const openCreate = () => { setEditPost(null); setDialogOpen(true); };

  return (
    <div data-testid="admin-blog-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400 text-sm">{posts.length} posts total</p>
        <Button onClick={openCreate} className="bg-[#FF2D78] hover:bg-[#ff4d8d]" data-testid="add-blog-btn">
          <Plus size={16} className="mr-2" /> New Post
        </Button>
      </div>
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="flex items-center justify-between p-4 bg-[#0a0a0a] border border-white/10" data-testid={`blog-row-${p.id}`}>
            <div className="flex items-center gap-4">
              {p.featured_image && <img src={p.featured_image} alt="" className="w-14 h-14 object-cover rounded" />}
              <div>
                <h3 className="text-white font-bold text-sm">{p.title}</h3>
                <p className="text-gray-500 text-xs">{p.city_tag} · {p.read_time} min read · /{p.slug}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={p.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                {p.is_published ? 'Published' : 'Draft'}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-blog-${p.id}`}><Edit size={14} className="text-gray-400" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} data-testid={`delete-blog-${p.id}`}><Trash2 size={14} className="text-red-400" /></Button>
            </div>
          </div>
        ))}
      </div>
      {posts.length === 0 && <p className="text-gray-500 text-center py-10">No blog posts yet</p>}
      <BlogFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} post={editPost} onRefresh={onRefresh} />
    </div>
  );
};

const BlogFormDialog = ({ open, onClose, post, onRefresh }) => {
  const isEdit = !!post;
  const defaultForm = { title: '', slug: '', excerpt: '', content: '', city_tag: '', featured_image: '', read_time: 5, is_published: false };
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (post) setForm({ title: post.title, slug: post.slug, excerpt: post.excerpt, content: post.content, city_tag: post.city_tag, featured_image: post.featured_image, read_time: post.read_time, is_published: post.is_published });
    else setForm(defaultForm);
  }, [post, open]);

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug) { toast.error('Title and slug are required'); return; }
    setSaving(true);
    try {
      if (isEdit) await axios.put(`${API}/blog/${post.id}`, form);
      else await axios.post(`${API}/blog`, form);
      toast.success(isEdit ? 'Updated!' : 'Created!');
      onClose(); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-xl uppercase">{isEdit ? 'Edit Post' : 'New Post'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSave} className="space-y-4 mt-2" data-testid="blog-form">
          <div>
            <Label className="text-gray-400 text-xs">Title</Label>
            <Input value={form.title} onChange={e => { f('title', e.target.value); if (!isEdit) f('slug', autoSlug(e.target.value)); }} className="bg-white/5 border-white/10 mt-1" data-testid="blog-title-input" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Slug</Label>
            <Input value={form.slug} onChange={e => f('slug', e.target.value)} className="bg-white/5 border-white/10 mt-1" data-testid="blog-slug-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400 text-xs">City Tag</Label>
              <Input value={form.city_tag} onChange={e => f('city_tag', e.target.value)} placeholder="e.g. Miami, General" className="bg-white/5 border-white/10 mt-1" data-testid="blog-city-input" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Read Time (min)</Label>
              <Input type="number" value={form.read_time} onChange={e => f('read_time', parseInt(e.target.value) || 0)} className="bg-white/5 border-white/10 mt-1" data-testid="blog-readtime-input" />
            </div>
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Excerpt</Label>
            <Textarea value={form.excerpt} onChange={e => f('excerpt', e.target.value)} className="bg-white/5 border-white/10 mt-1 min-h-[60px]" data-testid="blog-excerpt-input" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Content</Label>
            <Textarea value={form.content} onChange={e => f('content', e.target.value)} className="bg-white/5 border-white/10 mt-1 min-h-[200px] font-mono text-xs" data-testid="blog-content-input" />
          </div>
          <FileUpload value={form.featured_image} onChange={(v) => f('featured_image', v)} label="Featured Image" accept="image/*" />
          <div className="flex items-center gap-2">
            <Switch checked={form.is_published} onCheckedChange={v => f('is_published', v)} data-testid="blog-published-toggle" />
            <Label className="text-gray-400 text-xs flex items-center gap-1">
              {form.is_published ? <><Eye size={12} /> Published</> : <><EyeOff size={12} /> Draft</>}
            </Label>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#FF2D78] hover:bg-[#ff4d8d]" data-testid="blog-save-btn">{saving ? 'Saving...' : isEdit ? 'Update Post' : 'Create Post'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ==================== BOOKINGS ==================== */
const BookingsPanel = ({ bookings, crawls, onRefresh }) => {
  const crawlMap = Object.fromEntries(crawls.map(c => [c.id, c]));
  const updateStatus = async (id, status) => {
    try { await axios.put(`${API}/bookings/${id}/status?status=${status}`); toast.success('Updated'); onRefresh(); }
    catch { toast.error('Failed'); }
  };
  return (
    <div data-testid="admin-bookings-panel" className="overflow-x-auto">
      <table className="w-full">
        <thead><tr className="border-b border-white/10 text-gray-500 text-xs uppercase">
          <th className="text-left py-3 px-3">Customer</th><th className="text-left py-3 px-3">Crawl</th>
          <th className="text-left py-3 px-3">Tickets</th><th className="text-left py-3 px-3">Total</th>
          <th className="text-left py-3 px-3">Status</th><th className="text-left py-3 px-3">Actions</th>
        </tr></thead>
        <tbody>
          {bookings.map(b => (
            <tr key={b.id} className="border-b border-white/5">
              <td className="py-3 px-3"><p className="text-white text-sm">{b.customer_name}</p><p className="text-gray-500 text-xs">{b.customer_email}</p></td>
              <td className="py-3 px-3 text-gray-300 text-sm">{crawlMap[b.crawl_id]?.city || b.crawl_id.slice(0,8)}</td>
              <td className="py-3 px-3"><Badge className="bg-[#1E90FF]/20 text-[#1E90FF]">{b.quantity}x {b.ticket_type?.toUpperCase()}</Badge></td>
              <td className="py-3 px-3 text-white font-bold">${b.total_price}</td>
              <td className="py-3 px-3">
                <Badge className={b.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>{b.status}</Badge>
                {b.payment_session_id && <Badge className="bg-purple-500/20 text-purple-400 ml-1">Stripe</Badge>}
              </td>
              <td className="py-3 px-3">
                {b.status === 'pending' && <Button size="sm" onClick={() => updateStatus(b.id, 'confirmed')} className="bg-green-600 hover:bg-green-700 text-xs h-7"><Check size={12} className="mr-1" />Confirm</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {bookings.length === 0 && <p className="text-gray-500 text-center py-10">No bookings yet</p>}
    </div>
  );
};

/* ==================== SUBSCRIBERS ==================== */
const SubscribersPanel = ({ subscribers, allLeads }) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  const enriched = allLeads.map(s => {
    const phone = (s.phone || '').replace(/\D/g, '');
    const ac = phone.length >= 10 ? phone.slice(0, 3) : '';
    const loc = ac ? lookupAreaCode(ac) : { city: '', state: '' };
    return { ...s, areaCode: ac, areaCity: loc.city, areaState: loc.state };
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const filtered = enriched.filter(s => {
    if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (s.email || '').toLowerCase().includes(q) || (s.phone || '').includes(q) || (s.areaCode || '').includes(q) || s.areaCity.toLowerCase().includes(q) || s.areaState.toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'email') return dir * (a.email || '').localeCompare(b.email || '');
    if (sortBy === 'phone') return dir * (a.phone || '').localeCompare(b.phone || '');
    if (sortBy === 'areaCode') return dir * (a.areaCode || '').localeCompare(b.areaCode || '');
    if (sortBy === 'city') return dir * a.areaCity.localeCompare(b.areaCity);
    if (sortBy === 'state') return dir * a.areaState.localeCompare(b.areaState);
    if (sortBy === 'source') return dir * (a.source || '').localeCompare(b.source || '');
    return dir * ((a.created_at || '').localeCompare(b.created_at || ''));
  });

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads-export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cbc_leads.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const SortHeader = ({ col, label }) => (
    <th className="text-left py-3 px-3 cursor-pointer select-none" onClick={() => toggleSort(col)}>
      <span className="inline-flex items-center gap-1">{label} <ArrowUpDown size={12} className={sortBy === col ? 'text-[#FF2D78]' : 'text-gray-600'} /></span>
    </th>
  );

  const sourceLabel = (src) => src === 'the_drop' ? 'Get The Drop' : src === 'notify' ? 'Notify Me' : src || '--';

  return (
    <div data-testid="admin-subscribers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <p className="text-gray-400 text-sm">{allLeads.length} leads total</p>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="bg-white/5 border border-white/10 text-white text-xs px-2 py-1.5 rounded" data-testid="leads-source-filter">
            <option value="all">All Sources</option>
            <option value="notify">Notify Me</option>
            <option value="the_drop">Get The Drop</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <Input placeholder="Search email, phone, area code, city..." value={filter} onChange={e => setFilter(e.target.value)} className="bg-white/5 border-white/10 w-72 text-white" data-testid="leads-search" />
          <Button onClick={handleExport} variant="outline" className="border-[#B8FF2E] text-[#B8FF2E] hover:bg-[#B8FF2E]/10" data-testid="leads-export-btn">
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs uppercase text-gray-500">
          <thead><tr className="border-b border-white/10">
            <SortHeader col="date" label="Date" />
            <SortHeader col="email" label="Email" />
            <SortHeader col="phone" label="Phone" />
            <SortHeader col="areaCode" label="Area Code" />
            <SortHeader col="city" label="City" />
            <SortHeader col="state" label="State" />
            <SortHeader col="source" label="Source" />
          </tr></thead>
          <tbody>
            {sorted.map(s => (
              <tr key={s.id} className="border-b border-white/5 text-sm normal-case">
                <td className="py-2.5 px-3 text-gray-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '--'}</td>
                <td className="py-2.5 px-3 text-white">{s.email || '--'}</td>
                <td className="py-2.5 px-3 text-gray-300">{s.phone || '--'}</td>
                <td className="py-2.5 px-3"><Badge className="bg-[#FF2D78]/20 text-[#FF2D78]">{s.areaCode || '--'}</Badge></td>
                <td className="py-2.5 px-3 text-gray-300">{s.areaCity || '--'}</td>
                <td className="py-2.5 px-3 text-gray-300">{s.areaState || '--'}</td>
                <td className="py-2.5 px-3"><Badge className="bg-[#00F5FF]/20 text-[#00F5FF]">{sourceLabel(s.source)}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length === 0 && <p className="text-gray-500 text-center py-10">No leads found</p>}
    </div>
  );
};

/* ==================== INQUIRIES (GROUP BOOKINGS) ==================== */
const InquiriesPanel = ({ inquiries, onRefresh }) => {
  const updateStatus = async (id, status) => {
    try { await axios.put(`${API}/inquiries/${id}/status?status=${status}`); toast.success('Updated'); onRefresh(); }
    catch { toast.error('Failed'); }
  };
  const statuses = ['new', 'contacted', 'booked', 'closed'];
  return (
    <div data-testid="admin-inquiries-panel" className="space-y-3">
      {inquiries.map(inq => (
        <div key={inq.id} className="p-4 bg-[#0a0a0a] border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-white font-bold text-sm">{inq.name}</h3>
              <p className="text-gray-500 text-xs">{inq.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {statuses.map(s => (
                <button key={s} onClick={() => updateStatus(inq.id, s)}
                  className={`px-3 py-1 text-xs uppercase tracking-wider transition-colors ${
                    inq.status === s ? 'bg-[#FF2D78] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-4 text-xs text-gray-400 mt-2">
            <span className="text-[#1E90FF]">{inq.event_type}</span>
            <span>{inq.group_size} people</span>
            <span>{inq.city}</span>
            <span>{inq.preferred_date}</span>
          </div>
          {inq.message && <p className="text-gray-300 text-sm mt-2 bg-white/5 p-3">{inq.message}</p>}
        </div>
      ))}
      {inquiries.length === 0 && <p className="text-gray-500 text-center py-10">No group booking inquiries</p>}
    </div>
  );
};

/* ==================== GALLERY ==================== */
const GalleryPanel = ({ gallery, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(gallery.map(g => ({ ...g }))); }, [gallery]);

  const addItem = () => setItems([...items, { image_url: '', height: 280, tint: false }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, key, val) => { const n = [...items]; n[i] = { ...n[i], [key]: val }; setItems(n); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = items.map(({ image_url, height, tint }) => ({ image_url, height, tint }));
      await axios.put(`${API}/admin/gallery`, payload);
      toast.success('Gallery saved!'); onRefresh();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-gallery-panel">
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400 text-sm">{items.length} images</p>
        <div className="flex gap-3">
          <Button onClick={addItem} className="bg-white/10 hover:bg-white/20 text-white"><Plus size={14} className="mr-1" /> Add Image</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#FF2D78] hover:bg-[#ff4d8d]">{saving ? 'Saving...' : 'Save Gallery'}</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item, i) => (
          <div key={i} className="bg-[#0a0a0a] border border-white/10 overflow-hidden">
            <div className="p-3 space-y-2">
              <FileUpload value={item.image_url} onChange={(v) => updateItem(i, 'image_url', v)} label={`Image ${i + 1}`} />
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <Label className="text-gray-400 text-xs">Height</Label>
                  <select value={item.height} onChange={e => updateItem(i, 'height', parseInt(e.target.value))} className="bg-white/5 border border-white/10 text-white text-xs px-2 py-1">
                    <option value={200}>200px</option><option value={280}>280px</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-gray-400 text-xs">Pink Tint</Label>
                  <Switch checked={item.tint} onCheckedChange={v => updateItem(i, 'tint', v)} />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeItem(i)}><Trash2 size={14} className="text-red-400" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================== SITE CONFIG ==================== */
const SiteConfigPanel = ({ config, onRefresh }) => {
  const [cityPills, setCityPills] = useState([]);
  const [howItWorks, setHowItWorks] = useState([]);
  const [notifEmails, setNotifEmails] = useState([]);
  const [newCity, setNewCity] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setCityPills(config.city_pills || []);
      setHowItWorks(config.how_it_works || []);
      setNotifEmails(config.notification_emails || []);
    }
  }, [config]);

  const addCity = () => { if (newCity.trim()) { setCityPills([...cityPills, newCity.trim()]); setNewCity(''); } };
  const removeCity = (i) => setCityPills(cityPills.filter((_, idx) => idx !== i));
  const updateStep = (i, key, val) => { const n = [...howItWorks]; n[i] = { ...n[i], [key]: val }; setHowItWorks(n); };
  const addEmail = () => { if (newEmail.trim() && newEmail.includes('@')) { setNotifEmails([...notifEmails, newEmail.trim()]); setNewEmail(''); } };
  const removeEmail = (i) => setNotifEmails(notifEmails.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, { city_pills: cityPills, how_it_works: howItWorks, notification_emails: notifEmails });
      toast.success('Config saved!'); onRefresh();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-config-panel" className="space-y-8">
      {/* Notification Emails */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6">
        <h3 className="text-white font-bold text-sm uppercase mb-2">Notification Emails</h3>
        <p className="text-gray-500 text-xs mb-4">Group booking inquiries will be emailed to these addresses.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {notifEmails.map((email, i) => (
            <span key={i} className="bg-[#FF2D78]/10 text-[#FF2D78] border border-[#FF2D78]/20 px-3 py-1.5 text-sm flex items-center gap-2 rounded">
              {email} <button onClick={() => removeEmail(i)} data-testid={`remove-notif-email-${i}`}><X size={12} className="text-gray-400 hover:text-red-400" /></button>
            </span>
          ))}
          {notifEmails.length === 0 && <span className="text-gray-600 text-xs italic">No recipients configured yet</span>}
        </div>
        <div className="flex gap-2">
          <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" className="bg-white/5 border-white/10 text-white w-64" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())} data-testid="notif-email-input" />
          <Button onClick={addEmail} className="bg-white/10 hover:bg-white/20 text-white" data-testid="add-notif-email-btn">Add</Button>
        </div>
      </div>

      {/* City Pills */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6">
        <h3 className="text-white font-bold text-sm uppercase mb-4">City Pills (below Find A Crawl)</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {cityPills.map((city, i) => (
            <span key={i} className="bg-white/10 text-white px-3 py-1.5 text-sm flex items-center gap-2">
              {city} <button onClick={() => removeCity(i)}><X size={12} className="text-gray-400 hover:text-red-400" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newCity} onChange={e => setNewCity(e.target.value)} placeholder="Add city..." className="bg-white/5 border-white/10 text-white w-48" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCity())} />
          <Button onClick={addCity} className="bg-white/10 hover:bg-white/20 text-white">Add</Button>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6">
        <h3 className="text-white font-bold text-sm uppercase mb-4">How It Works Steps</h3>
        <div className="space-y-4">
          {howItWorks.map((step, i) => (
            <div key={i} className="grid grid-cols-[60px_1fr_2fr] gap-3 items-start">
              <Input value={step.step} onChange={e => updateStep(i, 'step', e.target.value)} className="bg-white/5 border-white/10 text-[#FF2D78] font-bold text-center" />
              <Input value={step.title} onChange={e => updateStep(i, 'title', e.target.value)} placeholder="Title" className="bg-white/5 border-white/10 text-white" />
              <Input value={step.desc} onChange={e => updateStep(i, 'desc', e.target.value)} placeholder="Description" className="bg-white/5 border-white/10 text-white" />
            </div>
          ))}
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-[#FF2D78] hover:bg-[#ff4d8d] px-8">{saving ? 'Saving...' : 'Save Config'}</Button>
    </div>
  );
};

/* ==================== ANALYTICS (GA4) ==================== */
const GA_ID = 'G-ZDV98WJ8N9';

const AnalyticsPanel = () => {
  const reportSections = [
    {
      title: 'Realtime',
      desc: 'Live activity on your site right now',
      links: [
        { label: 'Realtime Overview', path: 'reports/realtime/overview' },
      ],
    },
    {
      title: 'Acquisition',
      desc: 'How users find your site',
      links: [
        { label: 'Acquisition Overview', path: 'reports/acquisition-overview' },
        { label: 'User Acquisition', path: 'reports/acquisition-user-acquisition-summary' },
        { label: 'Traffic Acquisition', path: 'reports/acquisition-traffic-acquisition-summary' },
      ],
    },
    {
      title: 'Engagement',
      desc: 'What users do on your site',
      links: [
        { label: 'Engagement Overview', path: 'reports/engagement-overview' },
        { label: 'Pages & Screens', path: 'reports/engagement-pages-and-screens' },
        { label: 'Events', path: 'reports/engagement-events' },
        { label: 'Conversions', path: 'reports/engagement-conversions' },
        { label: 'Landing Pages', path: 'reports/engagement-landing-page' },
      ],
    },
    {
      title: 'Monetization',
      desc: 'Revenue and purchases',
      links: [
        { label: 'Monetization Overview', path: 'reports/monetization-overview' },
        { label: 'Ecommerce Purchases', path: 'reports/monetization-ecommerce-purchases' },
      ],
    },
    {
      title: 'Retention',
      desc: 'How well you keep users coming back',
      links: [
        { label: 'Retention Overview', path: 'reports/retention-overview' },
      ],
    },
    {
      title: 'User Attributes',
      desc: 'Who your users are',
      links: [
        { label: 'Demographics Overview', path: 'reports/user-demographics-overview' },
        { label: 'Demographics Details', path: 'reports/user-demographics-detail' },
        { label: 'Audiences', path: 'reports/user-audiences' },
      ],
    },
    {
      title: 'Tech',
      desc: 'Devices, browsers, and platforms',
      links: [
        { label: 'Tech Overview', path: 'reports/user-technology-overview' },
        { label: 'Tech Details', path: 'reports/user-technology-detail' },
      ],
    },
    {
      title: 'Explore',
      desc: 'Custom analysis and funnels',
      links: [
        { label: 'Explorations', path: 'analysis/p-analysis-hub' },
      ],
    },
  ];

  const customEvents = [
    { event: 'page_view', desc: 'Every page load', page: 'All pages' },
    { event: 'cta_click', desc: 'Hero & section CTA buttons', page: 'Home' },
    { event: 'city_pill_click', desc: 'City filter pill clicks', page: 'Home' },
    { event: 'crawl_card_click', desc: 'Crawl card link clicks', page: 'Home, Crawls' },
    { event: 'crawl_view', desc: 'Crawl detail page loads', page: 'Crawl Detail' },
    { event: 'ticket_click', desc: 'Ticket type selection', page: 'Crawl Detail' },
    { event: 'begin_checkout', desc: 'Booking form submitted', page: 'Crawl Detail' },
    { event: 'purchase', desc: 'Booking confirmed', page: 'Crawl Detail' },
    { event: 'generate_lead', desc: 'Email/phone subscriber', page: 'Footer' },
    { event: 'laylo_click', desc: 'Laylo embed interaction', page: 'Home' },
    { event: 'group_inquiry', desc: 'Group booking form submit', page: 'Groups' },
    { event: 'contact_submit', desc: 'Contact form submit', page: 'Contact' },
    { event: 'social_click', desc: 'Social media link clicks', page: 'Footer' },
    { event: 'footer_subscribe', desc: 'Footer email subscribe', page: 'Footer' },
    { event: 'blog_read', desc: 'Blog post viewed', page: 'Blog' },
  ];

  const gaUrl = (path) => `https://analytics.google.com/analytics/web/#/${path}`;

  return (
    <div data-testid="admin-analytics-panel" className="space-y-6">
      {/* Status Bar */}
      <div className="bg-[#0a0a0a] border border-white/10 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
          <div>
            <h3 className="text-white font-bold text-sm">Google Analytics 4 — Active</h3>
            <p className="text-gray-500 text-xs mt-0.5">Measurement ID: <span className="text-[#00F5FF] font-mono">{GA_ID}</span> &middot; Tracking on all pages</p>
          </div>
        </div>
        <div className="flex gap-3">
          <a href={gaUrl('report/reportinghub')} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#FF2D78] hover:bg-[#ff4d8d]" data-testid="ga-dashboard-link"><BarChart3 size={14} className="mr-2" /> Open Full GA4 Dashboard</Button>
          </a>
          <a href={gaUrl('admin')} target="_blank" rel="noopener noreferrer">
            <Button className="bg-white/10 hover:bg-white/20 text-white"><Settings size={14} className="mr-2" /> GA4 Admin</Button>
          </a>
        </div>
      </div>

      {/* Report Sections Grid */}
      <div>
        <h3 className="text-white font-bold text-sm uppercase mb-4">All GA4 Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {reportSections.map(section => (
            <div key={section.title} className="bg-[#0a0a0a] border border-white/10 p-5">
              <h4 className="text-white font-bold text-sm mb-1">{section.title}</h4>
              <p className="text-gray-500 text-xs mb-3">{section.desc}</p>
              <div className="space-y-1.5">
                {section.links.map(link => (
                  <a
                    key={link.label}
                    href={gaUrl(link.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[#1E90FF] hover:text-[#FF2D78] text-xs transition-colors"
                  >
                    {link.label} &rarr;
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Events Table */}
      <div>
        <h3 className="text-white font-bold text-sm uppercase mb-4">Custom Events Being Tracked</h3>
        <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-gray-500 text-xs uppercase">
                <th className="text-left py-3 px-4">Event Name</th>
                <th className="text-left py-3 px-4">Description</th>
                <th className="text-left py-3 px-4">Fires On</th>
                <th className="text-left py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {customEvents.map(ev => (
                <tr key={ev.event} className="border-b border-white/5">
                  <td className="py-2.5 px-4 font-mono text-[#00F5FF] text-xs">{ev.event}</td>
                  <td className="py-2.5 px-4 text-gray-300 text-xs">{ev.desc}</td>
                  <td className="py-2.5 px-4 text-gray-400 text-xs">{ev.page}</td>
                  <td className="py-2.5 px-4"><span className="text-green-400 text-xs flex items-center gap-1"><Check size={12} /> Live</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enhanced Measurement */}
      <div>
        <h3 className="text-white font-bold text-sm uppercase mb-4">Auto-Tracked by GA4 (Enhanced Measurement)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            'Page Views', 'Scroll Depth', 'Outbound Clicks', 'Site Search',
            'Video Engagement', 'File Downloads', 'Form Interactions', 'Session Start',
            'First Visit', 'User Engagement', 'Browser Language', 'Screen Resolution',
            'Device Category', 'Operating System', 'Country / City', 'Traffic Source / Medium'
          ].map(item => (
            <div key={item} className="flex items-center gap-2 bg-[#0a0a0a] border border-white/10 px-3 py-2">
              <Check size={12} className="text-green-400 flex-shrink-0" />
              <span className="text-gray-300 text-xs">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Setup Tips */}
      <div className="bg-[#0a0a0a] border border-white/10 p-5">
        <h3 className="text-white font-bold text-sm uppercase mb-3">Recommended Next Steps</h3>
        <div className="space-y-2 text-xs text-gray-400">
          <p>&middot; <a href={gaUrl('admin/streams')} target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline">Verify your data stream</a> is receiving data (check Realtime report)</p>
          <p>&middot; <a href={gaUrl('admin/conversions')} target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline">Mark key events as conversions</a> — recommended: <span className="text-[#00F5FF] font-mono">purchase</span>, <span className="text-[#00F5FF] font-mono">generate_lead</span>, <span className="text-[#00F5FF] font-mono">group_inquiry</span></p>
          <p>&middot; <a href={gaUrl('admin/audiences')} target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline">Create audiences</a> for remarketing (e.g., users who viewed a crawl but didn't book)</p>
          <p>&middot; <a href="https://support.google.com/analytics/answer/9304153" target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline">Link Google Ads</a> to track ad conversions and create remarketing lists</p>
          <p>&middot; <a href={gaUrl('admin/data-settings')} target="_blank" rel="noopener noreferrer" className="text-[#1E90FF] hover:underline">Set data retention</a> to 14 months for longer analysis windows</p>
        </div>
      </div>
    </div>
  );
};

/* ==================== PREVIEW ==================== */
const PreviewPanel = () => {
  const [mode, setMode] = useState('desktop');
  const previewUrl = window.location.origin;

  return (
    <div data-testid="admin-preview-panel">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setMode('desktop')} className={`flex items-center gap-2 px-4 py-2 text-sm ${mode === 'desktop' ? 'bg-[#FF2D78] text-white' : 'bg-white/5 text-gray-400'}`}>
          <Monitor size={16} /> Desktop
        </button>
        <button onClick={() => setMode('mobile')} className={`flex items-center gap-2 px-4 py-2 text-sm ${mode === 'mobile' ? 'bg-[#FF2D78] text-white' : 'bg-white/5 text-gray-400'}`}>
          <Smartphone size={16} /> Mobile
        </button>
      </div>

      {mode === 'desktop' ? (
        <div className="w-full border border-white/10 bg-white" style={{ height: '75vh' }}>
          <iframe src={previewUrl} title="Site Preview" className="w-full h-full border-none" />
        </div>
      ) : (
        <div className="flex justify-center py-8">
          {/* iPhone 16 Frame */}
          <div className="relative" style={{ width: '393px', height: '852px' }}>
            {/* Outer shell */}
            <div className="absolute inset-0 rounded-[55px] bg-[#1a1a1a] shadow-2xl" style={{ border: '2px solid #333' }}>
              {/* Side button — right (power) */}
              <div className="absolute -right-[3px] top-[180px] w-[3px] h-[80px] rounded-r-sm bg-[#2a2a2a]" />
              {/* Side buttons — left (volume up) */}
              <div className="absolute -left-[3px] top-[160px] w-[3px] h-[36px] rounded-l-sm bg-[#2a2a2a]" />
              {/* Side buttons — left (volume down) */}
              <div className="absolute -left-[3px] top-[210px] w-[3px] h-[36px] rounded-l-sm bg-[#2a2a2a]" />
              {/* Side button — left (action) */}
              <div className="absolute -left-[3px] top-[120px] w-[3px] h-[24px] rounded-l-sm bg-[#2a2a2a]" />

              {/* Inner bezel */}
              <div className="absolute inset-[3px] rounded-[52px] bg-black overflow-hidden">
                {/* Dynamic Island */}
                <div className="absolute top-[12px] left-1/2 -translate-x-1/2 z-20 w-[126px] h-[37px] bg-black rounded-full" />

                {/* Screen content */}
                <div className="absolute inset-0 rounded-[52px] overflow-hidden">
                  <iframe
                    src={previewUrl}
                    title="Mobile Preview"
                    className="w-full h-full border-none bg-white"
                    style={{ width: '393px', height: '852px' }}
                  />
                </div>

                {/* Home indicator bar */}
                <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 z-20 w-[140px] h-[5px] bg-white/30 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
